import { useState } from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Grid from '@mui/material/Grid'
import Divider from '@mui/material/Divider'
import CircularProgress from '@mui/material/CircularProgress'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import SendIcon from '@mui/icons-material/Send'
import { API_BASE_URL } from '../../config/env'
import { reportApiError } from '../../lib/reportApiError'

const STAKEHOLDER_TYPES = [
  'Private Sector Partner',
  'NGO / Non-profit',
  'Government / Public Sector',
  'Funder / Investor',
  'Advisory / Technical Support',
  'Research Institution',
  'Farmer',
]

interface FormData {
  name: string
  description: string
  stakeholderType: string
  website: string
  contactName: string
  contactRole: string
  contactEmail: string
  linkedin: string
}

interface FormErrors {
  name?: string
  description?: string
  stakeholderType?: string
  website?: string
  contactEmail?: string
}

const emptyForm: FormData = {
  name: '',
  description: '',
  stakeholderType: '',
  website: '',
  contactName: '',
  contactRole: '',
  contactEmail: '',
  linkedin: '',
}

function validateEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function validateUrl(url: string) {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export default function SubmissionForm() {
  const [form, setForm] = useState<FormData>(emptyForm)
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const validate = (): boolean => {
    const newErrors: FormErrors = {}
    if (!form.name.trim()) newErrors.name = 'Organisation name is required.'
    if (!form.description.trim()) newErrors.description = 'A description is required.'
    if (!form.stakeholderType) newErrors.stakeholderType = 'Please select a stakeholder type.'
    if (form.website && !validateUrl(form.website))
      newErrors.website = 'Please enter a valid URL (e.g. https://example.com).'
    if (form.contactEmail && !validateEmail(form.contactEmail))
      newErrors.contactEmail = 'Please enter a valid email address.'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }))
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const handleSubmit = async (e: { preventDefault(): void }) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    setSubmitError(null)
    try {
      let res: Response
      try {
        res = await fetch(`${API_BASE_URL}/organisations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        })
      } catch (err) {
        reportApiError(err, { endpoint: '/organisations' })
        setSubmitError('Unable to submit. Please check your connection and try again.')
        return
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        const apiMessage = (data as { error?: string }).error
        reportApiError(new Error(apiMessage ?? `Request failed (${res.status})`), {
          endpoint: '/organisations',
          status: res.status,
          statusText: res.statusText,
        })
        setSubmitError(apiMessage ?? 'Something went wrong. Please try again.')
        return
      }
      setSubmitted(true)
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 8,
          px: 4,
          backgroundColor: 'background.paper',
          borderRadius: 3,
          border: '1px solid rgba(27, 31, 29, 0.1)',
          boxShadow: '0 18px 36px rgba(27, 31, 29, 0.08)',
        }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" sx={{ mb: 2 }}>
          Thank you for your submission!
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, maxWidth: 520, mx: 'auto' }}>
          Your organisation has been submitted for review. We will be in touch shortly once we have reviewed the details.
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => { setForm(emptyForm); setSubmitted(false) }}
        >
          Submit another organisation
        </Button>
      </Box>
    )
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: 3,
        border: '1px solid rgba(27, 31, 29, 0.1)',
        p: { xs: 3, md: 4 },
        boxShadow: '0 18px 36px rgba(27, 31, 29, 0.08)',
      }}
    >
      <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700 }}>
        Organisation Details
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Fields marked with * are required.
      </Typography>

      <Grid container spacing={3}>
        <Grid size={12}>
          <TextField
            label="Organisation Name *"
            fullWidth
            value={form.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            variant="outlined"
          />
        </Grid>
        <Grid size={12}>
          <TextField
            label="Description *"
            fullWidth
            multiline
            rows={4}
            value={form.description}
            onChange={handleChange('description')}
            error={!!errors.description}
            helperText={errors.description || 'Briefly describe the organisation and its work in regenerative agriculture.'}
            variant="outlined"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            label="Stakeholder Type *"
            fullWidth
            value={form.stakeholderType}
            onChange={handleChange('stakeholderType')}
            error={!!errors.stakeholderType}
            helperText={errors.stakeholderType}
            variant="outlined"
          >
            {STAKEHOLDER_TYPES.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Website URL"
            fullWidth
            placeholder="https://example.com"
            value={form.website}
            onChange={handleChange('website')}
            error={!!errors.website}
            helperText={errors.website}
            variant="outlined"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>
        Contact Details (optional)
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Contact Name"
            fullWidth
            value={form.contactName}
            onChange={handleChange('contactName')}
            variant="outlined"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Contact Role / Title"
            fullWidth
            value={form.contactRole}
            onChange={handleChange('contactRole')}
            variant="outlined"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Contact Email"
            fullWidth
            type="email"
            value={form.contactEmail}
            onChange={handleChange('contactEmail')}
            error={!!errors.contactEmail}
            helperText={errors.contactEmail}
            variant="outlined"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="LinkedIn Profile URL"
            fullWidth
            placeholder="https://linkedin.com/in/..."
            value={form.linkedin}
            onChange={handleChange('linkedin')}
            variant="outlined"
          />
        </Grid>
      </Grid>

      {Object.keys(errors).length > 0 && (
        <Alert severity="error" sx={{ mt: 3 }}>
          Please fix the errors above before submitting.
        </Alert>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {submitError}
        </Alert>
      )}

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          disabled={loading}
          endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
          sx={{ px: 4 }}
        >
          {loading ? 'Submitting…' : 'Submit Organisation'}
        </Button>
      </Box>
    </Box>
  )
}
